import {Input} from "@adleritech/web-ui";
import IconCurrentLocation from "@adleritech/web-ui/assets/icon-current-location.svg?react";
import {type ReactNode, useRef, useState} from "react";
import styles from "./LocationInput.module.css";

type LocationInputProps = {
    id: string;
    defaultValue?: string;
    onChange: (value: string) => void;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onEnterKeyDown?: () => void;
    onCurrentLocation: () => void;
    onClear: () => void;
    placeholder?: string;
    disabled?: boolean;
    startIcon?: ReactNode;
    inputRef?: React.RefObject<HTMLInputElement | null>;
    withCurrentLocationButton?: boolean;
};

export function LocationInput({
    id,
    defaultValue,
    onChange,
    onFocus,
    onEnterKeyDown,
    onCurrentLocation,
    onClear,
    placeholder,
    disabled,
    startIcon,
    inputRef,
    withCurrentLocationButton,
}: LocationInputProps) {
    const [isFocused, setIsFocused] = useState(false);
    const isClickingClearButton = useRef(false);
    const clearButtonTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        onFocus?.(e);
    };

    const handleBlur = () => {
        if (isClickingClearButton.current) {
            return;
        }
        setIsFocused(false);
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        const isClearButton = target.closest('button[aria-label="Clear input"]') != null;

        if (isClearButton) {
            isClickingClearButton.current = true;

            if (clearButtonTimeoutRef.current) {
                clearTimeout(clearButtonTimeoutRef.current);
            }

            clearButtonTimeoutRef.current = setTimeout(() => {
                isClickingClearButton.current = false;
            }, 100);
        }
    };

    const handleClear = () => {
        if (inputRef?.current) {
            inputRef.current.value = "";
        }
        onChange("");
        onClear();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && onEnterKeyDown) {
            e.preventDefault();
            onEnterKeyDown();
        }
    };

    return (
        <div onMouseDown={handleMouseDown}>
            <Input
                id={id}
                ref={inputRef}
                defaultValue={defaultValue}
                onChange={(e) => onChange(e.target.value)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onClear={handleClear}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                disabled={disabled}
                type="search"
                autoComplete="off"
                clearable
                startIcon={startIcon}
                endIcon={
                    withCurrentLocationButton && isFocused ? (
                        <IconCurrentLocation
                            className={styles.iconCurrentLocation}
                            onClick={onCurrentLocation}
                            onMouseDown={(e) => e.preventDefault()}
                        />
                    ) : null
                }
            />
        </div>
    );
}
